import React, { useMemo, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { ArrowLeft } from 'lucide-react';
import { toast } from 'sonner';
import { useAuth } from '../contexts/AuthContext';
import { Button } from '../components/ui/button';
import { Checkbox } from '../components/ui/checkbox';
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from '../components/ui/dialog';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';

export default function Login() {
  const navigate = useNavigate();
  const { login, resetPassword } = useAuth();

  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [notRobot, setNotRobot] = useState(false);
  const [loading, setLoading] = useState(false);

  const [resetOpen, setResetOpen] = useState(false);
  const [resetEmail, setResetEmail] = useState('');
  const [resetPasswordValue, setResetPasswordValue] = useState('');
  const [resetPasswordConfirm, setResetPasswordConfirm] = useState('');
  const [resetLoading, setResetLoading] = useState(false);

  const loginDisabled = useMemo(
    () => loading || !email.trim() || !password.trim() || !notRobot,
    [email, loading, notRobot, password]
  );

  const handleSubmit = async (event) => {
    event.preventDefault();
    if (!notRobot) {
      toast.error('Lütfen "Ben robot değilim" kutusunu işaretleyin.');
      return;
    }

    setLoading(true);
    const result = await login(email, password);
    setLoading(false);

    if (result.success) {
      toast.success('Giriş başarılı.');
      navigate('/dashboard');
      return;
    }

    toast.error(result.error);
  };

  const handleResetPassword = async (event) => {
    event.preventDefault();

    if (resetPasswordValue.length < 6) {
      toast.error('Yeni şifre en az 6 karakter olmalı.');
      return;
    }
    if (resetPasswordValue !== resetPasswordConfirm) {
      toast.error('Şifreler eşleşmiyor.');
      return;
    }

    setResetLoading(true);
    const result = await resetPassword(resetEmail, resetPasswordValue);
    setResetLoading(false);

    if (result.success) {
      toast.success(result.message || 'Şifre sıfırlandı.');
      setResetOpen(false);
      setPassword('');
      setResetPasswordValue('');
      setResetPasswordConfirm('');
      return;
    }

    toast.error(result.error);
  };

  return (
    <div className="min-h-screen bg-[#050505] grid grid-cols-1 lg:grid-cols-2">
      <div
        className="hidden lg:block relative"
        style={{
          backgroundImage:
            'url(https://static.prod-images.emergentagent.com/jobs/d9bd63b7-d6a6-43cd-a29b-1a59dd534490/images/d9d097cd520df86f7eb413c9bbf5b0402cd9aed917b105693a21b63261712c59.png)',
          backgroundSize: 'cover',
          backgroundPosition: 'center',
        }}
      >
        <div className="absolute inset-0 bg-black/45 flex items-center justify-center p-12">
          <div className="text-center max-w-xl">
            <h2
              className="text-4xl sm:text-5xl font-black tracking-tighter text-white mb-4"
              style={{ fontFamily: 'Outfit, sans-serif' }}
            >
              Karga Akademi&apos;ye Hoş Geldin
            </h2>
            <p className="text-lg text-[#A1A1AA]">
              Premium mentorluk sistemi ile çizelgeni, mentorünü ve ilerlemeni tek ekrandan yönet.
            </p>
          </div>
        </div>
      </div>

      <div className="flex items-center justify-center p-6 lg:p-12">
        <div className="w-full max-w-md">
          <Link
            to="/"
            className="inline-flex items-center gap-2 text-[#A1A1AA] hover:text-[#B026FF] transition-colors mb-6"
            data-testid="back-to-home"
          >
            <ArrowLeft className="w-4 h-4" /> Ana Sayfaya Dön
          </Link>

          <div className="mb-8">
            <Link to="/" className="mb-6 flex items-center pl-1">
              <span className="text-3xl font-black" style={{ fontFamily: 'Outfit, sans-serif' }}>
                KARGA AKADEMİ
              </span>
            </Link>
            <h1
              className="text-3xl sm:text-4xl font-black tracking-tight mb-2"
              style={{ fontFamily: 'Outfit, sans-serif' }}
              data-testid="login-heading"
            >
              Giriş Yap
            </h1>
            <p className="text-[#A1A1AA]">Hesabına giriş yap ve çizelgeni görüntüle.</p>
          </div>

          <form onSubmit={handleSubmit} className="space-y-6" data-testid="login-form">
            <div>
              <Label htmlFor="email" className="text-white mb-2 block">
                E-posta
              </Label>
              <Input
                id="email"
                type="email"
                value={email}
                onChange={(event) => setEmail(event.target.value)}
                required
                className="bg-[#0A0A0A] border-white/10 focus:border-[#B026FF] focus:ring-1 focus:ring-[#B026FF] text-white"
                placeholder="ornek@email.com"
                data-testid="login-email-input"
              />
            </div>

            <div>
              <div className="flex items-center justify-between mb-2">
                <Label htmlFor="password" className="text-white block">
                  Şifre
                </Label>
                <button
                  type="button"
                  onClick={() => {
                    setResetEmail(email);
                    setResetOpen(true);
                  }}
                  className="text-sm text-[#B026FF] hover:text-[#C966FF]"
                >
                  Şifremi Unuttum
                </button>
              </div>
              <Input
                id="password"
                type="password"
                value={password}
                onChange={(event) => setPassword(event.target.value)}
                required
                className="bg-[#0A0A0A] border-white/10 focus:border-[#B026FF] focus:ring-1 focus:ring-[#B026FF] text-white"
                placeholder="••••••••"
                data-testid="login-password-input"
              />
            </div>

            <label className="flex items-center gap-3 rounded-lg border border-white/10 bg-white/5 px-4 py-3 cursor-pointer">
              <Checkbox
                checked={notRobot}
                onCheckedChange={(value) => setNotRobot(Boolean(value))}
                className="border-white/30 data-[state=checked]:bg-[#B026FF] data-[state=checked]:border-[#B026FF]"
              />
              <span className="text-sm text-white">Ben robot değilim</span>
            </label>

            <Button
              type="submit"
              disabled={loginDisabled}
              className="w-full bg-[#B026FF] hover:bg-[#C966FF] text-white shadow-lg shadow-[#B026FF]/20"
              data-testid="login-submit-btn"
            >
              {loading ? 'Giriş yapılıyor...' : 'Giriş Yap'}
            </Button>
          </form>

          <div className="mt-6 text-center space-y-3">
            <p className="rounded-lg border border-[#B026FF]/30 bg-[#B026FF]/10 px-4 py-3 text-sm text-[#F5E8FF]">
              🔐 BU SİTE MUKASOFT İLE KORUNMAKTADIR 💕
            </p>
            <p className="text-[#A1A1AA]">
              Hesabın yok mu?{' '}
              <Link to="/register" className="text-[#B026FF] hover:text-[#C966FF] font-semibold">
                Kaydol
              </Link>
            </p>
          </div>
        </div>
      </div>

      <Dialog open={resetOpen} onOpenChange={setResetOpen}>
        <DialogContent className="bg-[#0A0A0A] border-white/10 text-white">
          <DialogHeader>
            <DialogTitle className="text-xl font-black" style={{ fontFamily: 'Outfit, sans-serif' }}>
              Şifreni Sıfırla
            </DialogTitle>
            <DialogDescription className="text-[#A1A1AA]">
              Kullanıcı, mentor ve admin hesapları için yeni şifre oluşturabilirsin.
            </DialogDescription>
          </DialogHeader>

          <form onSubmit={handleResetPassword} className="space-y-4">
            <div>
              <Label htmlFor="reset-email" className="text-white mb-2 block">
                E-posta
              </Label>
              <Input
                id="reset-email"
                type="email"
                value={resetEmail}
                onChange={(event) => setResetEmail(event.target.value)}
                required
                className="bg-black/40 border-white/10 text-white"
              />
            </div>
            <div>
              <Label htmlFor="reset-password" className="text-white mb-2 block">
                Yeni Şifre
              </Label>
              <Input
                id="reset-password"
                type="password"
                value={resetPasswordValue}
                onChange={(event) => setResetPasswordValue(event.target.value)}
                required
                className="bg-black/40 border-white/10 text-white"
              />
            </div>
            <div>
              <Label htmlFor="reset-password-confirm" className="text-white mb-2 block">
                Yeni Şifre Tekrar
              </Label>
              <Input
                id="reset-password-confirm"
                type="password"
                value={resetPasswordConfirm}
                onChange={(event) => setResetPasswordConfirm(event.target.value)}
                required
                className="bg-black/40 border-white/10 text-white"
              />
            </div>

            <DialogFooter>
              <Button
                type="button"
                onClick={() => setResetOpen(false)}
                variant="outline"
                className="border-white/10 text-white"
              >
                Vazgeç
              </Button>
              <Button type="submit" disabled={resetLoading} className="bg-[#B026FF] hover:bg-[#C966FF] text-white">
                {resetLoading ? 'Kaydediliyor...' : 'Şifreyi Sıfırla'}
              </Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>
    </div>
  );
}
